import type { ReactNode } from "react";
import { MobileHeader } from "./MobileHeader";
import { PillNav } from "./PillNav";
import { HomeIndicator } from "./HomeIndicator";
import { useShellScroll } from "./usePillNavScroll";
import { BottomChromeProvider, usePillNavHidden } from "@/components/system/BottomChrome";

export interface MobileShellProps {
  children: ReactNode;
}

function MobileShellInner({ children }: MobileShellProps) {
  // W35.6: create / edit pages claim the bottom edge (see BottomChrome), so
  // the PillNav is suppressed there and the docked PageBottomActionBar takes
  // `bottom-0`. Everywhere else the pill stays and content keeps its pb-32
  // clearance.
  const pillNavHidden = usePillNavHidden();
  // W45.0 `ADR-0111`: ONE scroll listener for the whole shell. `data-scrolled`
  // lands on the shell root, and both the sticky topbar's material and the
  // nav's dormant/active step read it. Two listeners on the same event with two
  // thresholds is how the two rows get out of step — the topbar stays at rest
  // while the title slides underneath it, which is the defect itself.
  //
  // W47.4 `ADR-0139`: the same listener also drives `--scroll-vel` (the
  // progressive blur's dial) on this same root, for the same reason — a second
  // listener would be a second opinion about the same scroll. It writes through
  // a ref rather than state, so scrolling does not re-render the shell.
  const { scrolled, shellRef } = useShellScroll();

  return (
    <div
      ref={shellRef}
      data-component="MobileShell"
      data-scrolled={scrolled ? "true" : "false"}
      className="relative min-h-[100dvh] flex flex-col text-ink-1"
    >
      <MobileHeader />
      {/* App-wide horizontal gutter (per /impeccable 2026-09-22). PageHero uses
          `-mx-5` to bleed back to edge-to-edge for the iOS-style sticky header.
          Bottom padding clears PillNav (h-11 + bottom-4) + safe-area inset —
          dropped to `pb-8` when the pill is hidden, since the docked action bar
          now owns that space. */}
      <main className={pillNavHidden ? "flex-1 pb-8" : "flex-1 pb-32"}>{children}</main>
      {pillNavHidden ? null : <PillNav />}
      <HomeIndicator />
    </div>
  );
}

/**
 * Mobile shell (per ADR-0076 + `DESIGN.md §13` + `MOBILE.md §4`):
 *   MobileHeader + content + PillNav (1 avatar island + 3 pill tabs +
 *   1 menu island) + HomeIndicator safe-area.
 *
 * All 9 manifest modules remain reachable: 3 in the centre pill, 1 behind
 * the avatar island, 5 in the menu popover (consumed from the manifest's
 * `mobileSurface` field, not from a positional slice — ADR-0026's 5+4 split
 * would have put 日記 in the pill and 心願 in the popover).
 * The `SubRowNav` utility-strip was deleted per ADR-0019 — /profile is now
 * the avatar island, and pair / settings surface only inside the popover.
 *
 * W35.6: wrapped in `BottomChromeProvider` so a page can suppress the PillNav
 * when it docks its own bottom action bar.
 *
 * W45.0 `ADR-0111`: the shell root now carries `data-scrolled`. The **topbar is
 * the only sticky row** — `PageHero` dropped its own `sticky`, because two
 * elements sharing one `top-0` offset is not "one sticky bar", it is two
 * elements competing for the same 44px band, and the lower-z one slides
 * underneath. See the `.shell-bar` rules in `globals.css`.
 */
export function MobileShell({ children }: MobileShellProps) {
  return (
    <BottomChromeProvider>
      <MobileShellInner>{children}</MobileShellInner>
    </BottomChromeProvider>
  );
}
